import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Card, Tab, TabGroup, TabList, TabPanel, TabPanels, Text, Title,
} from '@tremor/react';
import { fetchLineCards, fetchVendors } from '../api/vendors';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import { currentUser } from '../api/auth';
import { t } from '../lib/i18n';
import type { LineCard, User, VendorScorecard } from '../api/types';
import Header from '../components/Header';
import VendorScorecardTable from '../components/VendorScorecardTable';
import VendorLineTable from '../components/VendorLineTable';
import VendorDetailModal from '../components/VendorDetailModal';

const fmtPct = new Intl.NumberFormat('es-MX', {
  style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1,
});

export default function Vendors() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  return user ? <VendorsInner user={user} /> : <Navigate to="/login" replace />;
}

export function VendorsInner({ user, embedded = false }: { user: User; embedded?: boolean }) {
  const [vendors, setVendors] = useState<VendorScorecard[] | null>(null);
  const [lineCards, setLineCards] = useState<LineCard[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<VendorScorecard | null>(null);

  const load = () => {
    let alive = true;
    setError(null);
    Promise.all([fetchVendors(), fetchLineCards()])
      .then(([v, lc]) => { if (alive) { setVendors(v); setLineCards(lc); } })
      .catch((e: Error) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  };
  useEffect(() => load(), []);

  const showCost = useMemo(() => {
    if (!vendors || vendors.length === 0) return user.role !== 'staff';
    return vendors.some((v) => v.cost_trend_pct !== undefined || v.total_ordered_mxn !== undefined);
  }, [vendors, user.role]);

  const kpis = useMemo(() => {
    let activeCount = 0;
    let sumQtyOrd = 0;
    let sumQtyRcv = 0;
    let sumLT = 0;
    let ltCount = 0;
    for (const v of vendors ?? []) {
      if (v.po_count > 0) activeCount += 1;
      if (v.fill_rate != null && (v.total_ordered_mxn ?? 0) > 0) {
        sumQtyOrd += v.total_ordered_mxn ?? 0;
        sumQtyRcv += (v.fill_rate ?? 0) * (v.total_ordered_mxn ?? 0);
      }
      if (v.avg_lead_time_days != null) {
        sumLT += v.avg_lead_time_days * v.po_count;
        ltCount += v.po_count;
      }
    }
    return {
      activeCount,
      totalLines: lineCards == null ? 0 : new Set(lineCards.map((c) => `${c.sid}-${c.line}`)).size,
      chainFillRate: sumQtyOrd > 0 ? sumQtyRcv / sumQtyOrd : null,
      chainLeadTime: ltCount > 0 ? sumLT / ltCount : null,
    };
  }, [vendors, lineCards]);

  const content = (
    <>
        <Title>{t.vendors.title}</Title>
        <Text className="text-tremor-content text-sm">{t.vendors.subtitle}</Text>

        {error && (
          <ErrorRetry
            message={error.includes('preparación') ? t.vendors.notReady : error}
            onRetry={load}
          />
        )}
        {!vendors && !error && <TableSkeleton rows={6} cols={5} />}

        {vendors && (
          <Card className={`grid grid-cols-2 ${showCost ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4`}>
            <Kpi label={t.vendors.kpi.activeVendors} value={String(kpis.activeCount)} />
            <Kpi label={t.vendors.kpi.lineCount}     value={String(kpis.totalLines)} />
            <Kpi label={t.vendors.kpi.chainFillRate} value={kpis.chainFillRate == null ? '—' : fmtPct.format(kpis.chainFillRate)} />
            <Kpi label={t.vendors.kpi.chainLeadTime} value={kpis.chainLeadTime == null ? '—' : kpis.chainLeadTime.toFixed(1)} />
          </Card>
        )}

        {vendors && lineCards && (
          <TabGroup>
            <TabList>
              <Tab>{t.vendors.tabs.byVendor}</Tab>
              <Tab>{t.vendors.tabs.byLine}</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <VendorScorecardTable rows={vendors} showCost={showCost} onSelect={setSelected} />
              </TabPanel>
              <TabPanel>
                <VendorLineTable lineCards={lineCards} vendors={vendors} showCost={showCost} />
              </TabPanel>
            </TabPanels>
          </TabGroup>
        )}

      {selected && lineCards && (
        <VendorDetailModal
          vendor={selected}
          lineCards={lineCards}
          showCost={showCost}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );

  // When embedded inside PurchasingLayout, the layout owns the Header and the
  // page wrapper. Standalone use (legacy /vendors route) keeps the full chrome.
  if (embedded) return <div className="space-y-4">{content}</div>;
  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        {content}
      </main>
    </div>
  );
}

function Kpi({ label, value }: { label: React.ReactNode; value: string }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className="text-tremor-content-strong text-lg font-semibold">{value}</div>
    </div>
  );
}
